import { queryOptions } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { encountersKeys } from "./keys";
import { RefillsByIdResponse, RefillsResponse } from "../types";
import { buildQueryString } from "~/utils/url";
import { formatFullDate, formatDate } from "@workspace/lib/date";
import { EncountersQueryOptions } from "../types";

type RefillsByIdQueryOptions = {
    id: string
}

export const refillsQueryOptions = (opts: EncountersQueryOptions) => {
    return queryOptions({
        queryKey: encountersKeys.refills(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/refills${buildQueryString(opts)}`);
                const result = await handleResponse<RefillsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
    });
};

export const todayEncounterRefillsQueryOptions = (opts: EncountersQueryOptions) => {
    return queryOptions({
        queryKey: encountersKeys.todayEncounterRefills(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/refills${buildQueryString(opts)}`);
                const result = await handleResponse<RefillsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return data.refills.map((refill) => ({
                id: refill.refill_id,
                meeting_type: "Needs Approval",
                title: refill.patient.name,
                date: formatFullDate(refill.request_datetime),
                medication_name: refill.medication.name,
                value: refill.medication.dosage,
            }));
        },
    });
};


export const getRefillsByIdOptions = (opts: RefillsByIdQueryOptions) => {
    return queryOptions({
        queryKey: encountersKeys.refillsById(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/refills/${opts.id}`);
                const result = await handleResponse<RefillsByIdResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },


        select(data) {
            // Check if data has the expected structure
            if (!data || !data.refill) {
                console.error("Invalid data structure:", data);
                return null;
            }

            try {
                // Check if this is a batch refill
                const isBatch = data.is_batch || (data.refill.refills && Array.isArray(data.refill.refills));

                if (isBatch && data.refill.refills) {
                    // Handle batch refills - return array of medications with individual URLs
                    const transformedData = {
                        id: data.refill.refill_batch_id || data.refill.refill_id,
                        refillBatchId: data.refill.refill_batch_id,
                        refillCount: data.refill.refill_count,
                        isBatch: true,
                        userName: data.refill.patient?.name,
                        userEmail: data.refill.patient?.email,
                        prescriptionId: data.refill.prescription_id,
                        // Don't combine URLs - each medication will have its own
                        imageUrls: [],
                        fileUrls: [],
                        medications: data.refill.refills.map(refill => ({
                            name: refill.medication?.name,
                            dosage: refill.medication?.dosage,
                            quantity: refill.medication?.quantity,
                            duration: refill.medication?.duration,
                            refills: refill.medication?.refills,
                            notes: refill.medication?.notes,
                            cost: refill.cost,
                            refillId: refill.refill_id,
                            deliveryStatus: refill.delivery_status,
                            deliveryDate: refill.delivery_date,
                            paymentStatus: refill.payment_status,
                            status: refill.refill_status,
                            type: refill.type,
                            // Each medication gets its own URLs
                            imageUrls: refill.urls?.images || [],
                            fileUrls: refill.urls?.files || []
                        })),
                        patient: {
                            name: data.refill.patient?.name,
                            age: data.refill.patient?.age,
                            user_id: data.refill.patient?.user_id,
                            user_dosespot_id: data.refill.patient?.user_dosespot_id,
                            gender: data.refill.patient?.gender,
                            email: data.refill.patient?.email
                        },
                        requestedDate: data.refill.refills[0]?.request_datetime || data.refill.request_date ? `${formatDate(data.refill.refills[0]?.request_datetime || data.refill.request_date)}` : '',
                        sideEffects: data.refill.side_effects || {},
                        weightHistory: data.refill.weight_history ? {
                            initialWeight: data.refill.weight_history.initial?.weight,
                            initialWeightDate: data.refill.weight_history.initial?.date,
                            currentWeight: data.refill.weight_history.current?.weight,
                            currentWeightDate: data.refill.weight_history.current?.date,
                            weightGoal: data.refill.weight_history.progress,
                        } : null,
                        status: data.refill.status,
                        paymentStatus: data.refill.refills[0]?.payment_status,
                        rejectionReason: data.refill.rejection_reason ?? null,
                        type: data.refill.type ?? null,
                    };
                    return transformedData;
                } else {
                    // Handle single refill - return single medication
                    const transformedData = {
                        id: data.refill.refill_id,
                        isBatch: false,
                        userName: data.refill.patient?.name,
                        userEmail: data.refill.patient?.email,
                        prescriptionId: data.refill.prescription_id,
                        medications: {
                            medication: data.refill.medication?.name,
                            medicationDosage: data.refill.medication?.dosage,
                            quantity: data.refill.medication?.quantity,
                            duration: data.refill.medication?.duration,
                            refills: data.refill.medication?.refills,
                            // notes: data.refill.medication?.notes ?? null,
                        },
                        imageUrls: data.refill.urls?.images ?? [],
                        fileUrls: data.refill.urls?.files ?? [],
                        patient: {
                            name: data.refill.patient?.name,
                            age: data.refill.patient?.age,
                            user_id: data.refill.patient?.user_id,
                            user_dosespot_id: data.refill.patient?.user_dosespot_id,
                            gender: data.refill.patient?.gender,
                            email: data.refill.patient?.email
                        },
                        requestedDate: data.refill.request_date ? `${formatDate(data.refill.request_date)}` : '',
                        sideEffects: data.refill.side_effects,
                        weightHistory: data.refill.weight_history ? {
                            initialWeight: data.refill.weight_history.initial?.weight,
                            initialWeightDate: data.refill.weight_history.initial?.date,
                            currentWeight: data.refill.weight_history.current?.weight,
                            currentWeightDate: data.refill.weight_history.current?.date,
                            weightGoal: data.refill.weight_history.progress,
                        } : null,
                        status: data.refill.status,
                        paymentStatus: data.refill.payment_status,
                        rejectionReason: data.refill.rejection_reason ?? null,
                        type: data.refill.type ?? null,
                    };
                    return transformedData;
                }
            } catch (error) {
                console.error("Error in select function:", error);
                console.error("Data that caused error:", data);
                return null;
            }
        }
    });
}
